// src/pages/Enseignants/Events/CreateEventModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FaSave,
  FaTimes,
  FaPlus,
  FaTrash,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaLayerGroup,
  FaFlag,
  FaUsers,
  FaExternalLinkAlt,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { createEvent } from "../../../services/eventServices";
import { getSettings } from "../../../services/settingsServices";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: [false, 3, 4] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};
const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

const CreateEventModal = ({ isOpen, onClose, refreshList }) => {
  const [formData, setFormData] = useState({
    titre: "",
    description: "",
    start: "",
    end: "",
    type_event: "",
    level_event: "",
    cate_event: [],
    lieu_event: "",
    horaire_event: "",
    photo_url: "",
    agenda_url: "",
    is_active: true,
  });

  const [selectedCate, setSelectedCate] = useState("");
  const [shortnameClub, setShortnameClub] = useState("");
  const [imgOk, setImgOk] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const data = await getSettings();
        if (data?.length > 0) setShortnameClub(data[0].shortname_club || "");
      } catch (error) {
        console.error("Erreur paramètres", error);
        toast.error("Erreur lors de la récupération des paramètres.");
      }
    })();
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddCategory = () => {
    if (selectedCate && !formData.cate_event.includes(selectedCate)) {
      setFormData((prev) => ({
        ...prev,
        cate_event: [...prev.cate_event, selectedCate],
      }));
      setSelectedCate("");
    }
  };
  const handleRemoveCategory = (category) => {
    setFormData((prev) => ({
      ...prev,
      cate_event: prev.cate_event.filter((c) => c !== category),
    }));
  };
  const handleClearCategories = () => {
    setFormData((prev) => ({ ...prev, cate_event: [] }));
  };

  // Validation simple avant submit
  const validateBeforeSubmit = () => {
    if (!formData.titre?.trim()) {
      toast.error("Le titre est requis.");
      return false;
    }
    if (!formData.start) {
      toast.error("La date de début est requise.");
      return false;
    }
    if (formData.end && new Date(formData.end) < new Date(formData.start)) {
      toast.error("La date de fin ne peut pas être antérieure à la date de début.");
      return false;
    }
    if (!formData.type_event) {
      toast.error("Le type d’événement est requis.");
      return false;
    }
    if (!formData.level_event) {
      toast.error("Le niveau de l’événement est requis.");
      return false;
    }
    return true;
    };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateBeforeSubmit()) return;

    try {
      const payload = {
        ...formData,
        cate_event: (formData.cate_event || []).join(","),
      };
      await createEvent(payload);
      toast.success("Événement créé avec succès.");
      refreshList?.();
      onClose?.();
      // Reset (optionnel)
      setFormData({
        titre: "",
        description: "",
        start: "",
        end: "",
        type_event: "",
        level_event: "",
        cate_event: [],
        lieu_event: "",
        horaire_event: "",
        photo_url: "",
        agenda_url: "",
        is_active: true,
      });
    } catch (error) {
      console.error("Erreur création événement", error);
      toast.error("Erreur lors de la création de l'événement.");
    }
  };

  // Listes
  const baseTypeEvents = useMemo(
    () => [
      "Compétition",
      "Animation",
      "Stage & Regroupement Technique",
      "Passage de Grade",
      "Entraînement Départemental",
      "Stage Sportif Départemental",
      "Tournoi Interclubs",
      "Cours commun Club",
      "Stage Club",
      shortnameClub || "Votre Club",
      "Autre",
    ],
    [shortnameClub]
  );
  const levelEvents = ["Club", "Départemental", "Régional", "National", "International"];
  const cateEvents = [
    "Mini-Poussin(e)",
    "Poussin(e)",
    "Pupille",
    "Benjamin(e)",
    "Minime",
    "Cadet(te)",
    "Junior",
    "Sénior",
    "Vétéran",
  ];

  if (!isOpen) return null;

  const formatFR = (d) =>
    d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      {/* Boîte modale 85vh, header sticky, scroll vertical */}
      <div className="relative w-full max-w-5xl h-[85vh] max-h-[85vh] overflow-y-auto rounded-2xl shadow-xl ring-1 ring-slate-200/70 dark:ring-slate-800/70 bg-white/90 dark:bg-slate-900/80">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 sm:px-7 py-4 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
          <h3 className="text-base sm:text-lg font-semibold">Créer un événement</h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 transition"
            aria-label="Fermer"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps */}
        <div className="grid md:grid-cols-3 gap-0">
          {/* Colonne Form (2/3) */}
          <div className="md:col-span-2 p-5 sm:p-7">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Titre de l’événement *
                </label>
                <input
                  type="text"
                  name="titre"
                  value={formData.titre}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="Ex : Compétition départementale"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="Informations utiles, consignes, documents..."
                />
              </div>

              {/* Dates */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Date de début *
                  </label>
                  <input
                    type="date"
                    name="start"
                    value={formData.start}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    name="end"
                    value={formData.end}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Type & Niveau */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Type *
                  </label>
                  <select
                    name="type_event"
                    value={formData.type_event}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    required
                  >
                    <option value="">Sélectionner</option>
                    {baseTypeEvents.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Niveau *
                  </label>
                  <select
                    name="level_event"
                    value={formData.level_event}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    required
                  >
                    <option value="">Sélectionner</option>
                    {levelEvents.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Catégories */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Catégories
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedCate}
                    onChange={(e) => setSelectedCate(e.target.value)}
                    className="flex-1 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {cateEvents.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={handleAddCategory} className="btn btn-primary rounded-xl">
                    <FaPlus />
                  </button>
                  {formData.cate_event.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearCategories}
                      className="text-xs underline opacity-80 hover:opacity-100"
                    >
                      Vider
                    </button>
                  )}
                </div>

                {/* Chips */}
                {formData.cate_event.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.cate_event.map((cate) => (
                      <span
                        key={cate}
                        className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 ring-1 ring-sky-200/70 dark:ring-sky-800/50"
                      >
                        {cate}
                        <button
                          type="button"
                          onClick={() => handleRemoveCategory(cate)}
                          className="opacity-80 hover:opacity-100"
                          aria-label={`Retirer ${cate}`}
                          title={`Retirer ${cate}`}
                        >
                          <FaTrash />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Lieu */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Lieu
                </label>
                <input
                  type="text"
                  name="lieu_event"
                  value={formData.lieu_event}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="Salle / adresse"
                />
              </div>

              {/* Horaires (Quill) */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Horaires (formaté)
                </label>
                <div className="rounded-xl overflow-hidden ring-1 ring-slate-300 dark:ring-slate-700">
                  <ReactQuill
                    value={formData.horaire_event}
                    onChange={(value) => setFormData((prev) => ({ ...prev, horaire_event: value }))}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Ex : Pesée 8h30, Début 9h30..."
                    theme="snow"
                  />
                </div>
              </div>

              {/* URL image & agenda */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    URL de la photo
                  </label>
                  <input
                    type="url"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={(e) => {
                      setImgOk(true);
                      handleChange(e);
                    }}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    placeholder="https://…"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    URL agenda (Google/ICS)
                  </label>
                  <input
                    type="url"
                    name="agenda_url"
                    value={formData.agenda_url}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    placeholder="https://…"
                  />
                </div>
              </div>

              {/* Actif */}
              <div className="flex items-center gap-2">
                <input
                  id="is_active"
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="toggle toggle-success"
                />
                <label htmlFor="is_active" className="text-sm">
                  Afficher l’événement (actif)
                </label>
              </div>

              {/* Actions */}
              <div className="pt-2 flex justify-center gap-2">
                <Button text="Enregistrer" icon={FaSave} type="submit" className="!rounded-xl gap-2" />
                <Button text="Annuler" icon={FaTimes} onClick={onClose} type="button" className="btn-ghost !rounded-xl gap-2" />
              </div>
            </form>
          </div>

          {/* Colonne Aperçu (1/3) */}
          <aside className="md:col-span-1 p-5 sm:p-7 bg-slate-50/60 dark:bg-slate-900/40 border-l border-slate-200 dark:border-slate-800">
            {/* Carte image */}
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60">
              <div className="p-3">
                <img
                  src={imgOk && formData.photo_url ? formData.photo_url : "/img/no-image.jpg"}
                  alt={formData.titre || "Aperçu"}
                  className="block w-full max-h-[38vh] object-contain rounded-md"
                  onError={() => setImgOk(false)}
                />
              </div>

              {/* Meta compacte */}
              <div className="px-4 pb-4 text-sm text-slate-700 dark:text-slate-200 space-y-2">
                {formData.type_event && (
                  <div className="flex items-center gap-2">
                    <FaLayerGroup className="opacity-70" />
                    <span className="truncate">{formData.type_event}</span>
                  </div>
                )}
                {formData.level_event && (
                  <div className="flex items-center gap-2">
                    <FaFlag className="opacity-70" />
                    <span className="truncate">{formData.level_event}</span>
                  </div>
                )}
                {formData.lieu_event && (
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt className="opacity-70" />
                    <span className="truncate">{formData.lieu_event}</span>
                  </div>
                )}
                {formData.cate_event?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <FaUsers className="opacity-70" />
                    <span className="truncate">{formData.cate_event.join(", ")}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <FaCalendarAlt className="opacity-70" />
                  <span className="truncate">
                    {formatFR(formData.start)}
                    {formData.end ? ` → ${formatFR(formData.end)}` : ""}
                  </span>
                </div>
                {formData.agenda_url && (
                  <a
                    href={formData.agenda_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs underline decoration-dotted mt-1"
                    title="Ouvrir l’agenda"
                  >
                    Ouvrir l’agenda <FaExternalLinkAlt className="opacity-70" />
                  </a>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CreateEventModal;
